import { WorkState, WorkProgressDto, WorkItemDto } from '@/api/generator';
import { lightThemeColors } from '@/styles/vuetify';
import {
  mdiCheckCircle,
  mdiAlertCircle,
  mdiCloseCircle,
  mdiCircle,
  mdiShare,
  mdiFormatListCheckbox,
} from '@mdi/js';

export type WorkProgressKey = keyof Omit<WorkProgressDto, '$type'>;

export function getWorkStateIcon(workState: WorkState, showOpenIcon: boolean = false) {
  return workState === 'Open' && !showOpenIcon ? undefined : workStateIcons[workState];
}

export function getProgressStateIcon(progressState: WorkProgressKey) {
  return workStateIcons[stateProgressMap[progressState]];
}

const stateProgressMap: Record<WorkProgressKey, WorkState> = {
  failedSteps: 'Failed',
  openSteps: 'Open',
  passedSteps: 'Passed',
  skippedSteps: 'Skipped',
  warningSteps: 'Warning',
  withoutEvaluation: 'WithoutEvaluation',
};

const workStateMap: Record<WorkState, WorkProgressKey> = {
  Failed: 'failedSteps',
  Open: 'openSteps',
  Passed: 'passedSteps',
  Skipped: 'skippedSteps',
  Warning: 'warningSteps',
  WithoutEvaluation: 'withoutEvaluation',
};

type IconProps = { icon: string; color: string };

const workStateIcons: Record<WorkState, IconProps> = {
  Passed: {
    icon: mdiCheckCircle,
    color: lightThemeColors.success,
  },
  Warning: {
    icon: mdiAlertCircle,
    color: lightThemeColors.warning,
  },
  Failed: {
    icon: mdiCloseCircle,
    color: lightThemeColors.error,
  },
  WithoutEvaluation: {
    icon: mdiCircle,
    color: lightThemeColors['primary-lighten-3'],
  },
  Skipped: {
    icon: mdiShare,
    color: lightThemeColors.info,
  },
  Open: {
    icon: mdiFormatListCheckbox,
    color: lightThemeColors['primary'],
  },
};

export function summarizeWorkItemProgress(workitem: WorkItemDto): WorkProgressDto {
  const steps = workitem.workSteps ?? [];

  return steps.reduce<WorkProgressDto>(
    (state, step) => {
      if (step.workState) {
        const progressKey = workStateMap[step.workState];
        return {
          ...state,
          [progressKey]: (state[progressKey] ?? 0) + 1,
        };
      } else {
        return {
          ...state,
          openSteps: (state.openSteps ?? 0) + 1,
        };
      }
    },
    {
      withoutEvaluation: 0,
      passedSteps: 0,
      warningSteps: 0,
      failedSteps: 0,
      openSteps: 0,
      skippedSteps: 0,
    },
  );
}
